import { useEffect, useRef, useState } from "react";
import { aboutStats, featuredGames } from "../../../mocks/gameData";

export default function AboutMe() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="relative py-28 bg-[#07070f] overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#e879f9] to-transparent opacity-40" />
      <div className="absolute -left-64 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#e879f9]/5 blur-3xl" />
      <div className="absolute -right-64 top-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-[#22d3ee]/5 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-16">
          <span className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-widest">// 01</span>
          <div className="h-px flex-1 max-w-xs bg-gradient-to-r from-[#e879f9]/40 to-transparent" />
          <h2 className="font-orbitron text-3xl md:text-4xl font-black uppercase text-white">
            About <span className="neon-text-fuchsia">Me</span>
          </h2>
          <div className="h-px flex-1 max-w-xs bg-gradient-to-l from-[#e879f9]/40 to-transparent" />
          <span className="font-mono-tech text-xs text-[#22d3ee] uppercase tracking-widest">02 //</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image side */}
          <div
            className={`relative transition-all duration-700 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="relative w-full h-[500px]">
              <div className="absolute inset-0 border-2 border-[#e879f9]/20 rounded-lg translate-x-4 translate-y-4" />
              <img
                src="https://public.readdy.ai/ai/img_res/1af5c474fccc76dd9daf938cdc749380.jpg"
                alt="Aidan Barrett at his gaming setup"
                className="w-full h-full object-cover object-top rounded-lg border border-[#e879f9]/20"
              />
              {/* Overlay label */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#07070f]/80 backdrop-blur-sm border border-[#e879f9]/20 rounded-md px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-orbitron text-sm font-bold text-white">Aidan Barrett</p>
                  <p className="font-mono-tech text-xs text-[#e879f9]">Pro Gamer &amp; Streamer</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#22d3ee] animate-pulse inline-block" />
                  <span className="font-mono-tech text-xs text-[#22d3ee]">Online</span>
                </div>
              </div>
            </div>
          </div>

          {/* Text side */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            <div className="space-y-5 mb-8">
              <p className="font-rajdhani text-lg text-slate-300 leading-relaxed">
                What&apos;s up — I&apos;m <span className="text-white font-semibold">Aidan Barrett</span>, a competitive gamer and full-time content creator from the ground up. 
                I&apos;ve been playing games my entire life and turned that passion into something real.
              </p>
              <p className="font-rajdhani text-lg text-slate-400 leading-relaxed">
                Whether I&apos;m grinding ranked ladders, attempting no-hit boss runs, or vibing with the community 
                on variety nights — I bring everything I&apos;ve got every single stream. No filters, no excuses.
              </p>
              <p className="font-rajdhani text-lg text-slate-400 leading-relaxed">
                My goal is simple: <span className="neon-text-cyan font-semibold">make gaming entertainment that slaps</span> and 
                build a community of people who love this stuff as much as I do.
              </p>
            </div>

            {/* Featured games */}
            <div className="mb-8">
              <p className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-widest mb-3">Current Rotation</p>
              <div className="flex flex-wrap gap-2">
                {featuredGames.map((game) => (
                  <span
                    key={game}
                    className="font-rajdhani text-sm font-semibold px-3 py-1 rounded-full border border-[#22d3ee]/30 text-[#22d3ee] bg-[#22d3ee]/5"
                  >
                    {game}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-4">
              {aboutStats.map((stat) => (
                <div
                  key={stat.label}
                  className="card-gaming rounded-lg px-5 py-4"
                >
                  <div className="font-orbitron text-2xl font-black neon-text-fuchsia">{stat.value}</div>
                  <div className="font-rajdhani text-sm uppercase tracking-wider text-slate-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
